/**
 * Hooks TanStack Query Nexus — un endpoint = un hook.
 *
 * Les schémas Zod restent au plus proche du backend (cf. packages/backend/src/routes).
 * En vrai monorepo on les exporterait depuis @nexus/shared, à faire en J4b-bis.
 */
import { NotificationKindSchema } from '@nexus/shared';
import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { api } from './api';
import { useAuth } from './auth';
import { HOME_QUERY_KEY } from './queryClient';
import {
  deleteProviderWebviewData,
  destroyProviderWebview,
  providerWebviewLabel,
  type WebviewProvider,
} from './tauri';
import { currentWeekBounds } from './week';

/**
 * Les hooks ci-dessous gatent leur `queryFn` avec `enabled: !!id` — TanStack
 * Query ne l'exécute donc jamais avec un id manquant, mais le typage
 * `string | undefined` du paramètre ne le reflète pas côté `queryFn`. Ce
 * garde-fou explicite remplace l'assertion non-null `id!` par une erreur
 * lisible si ce contrat venait à être violé (plutôt qu'un `/x/undefined/y`).
 */
function requireId(id: string | undefined, label: string): string {
  if (!id) throw new Error(`${label} id is required`);
  return id;
}

// ───────────────────────────── Groups ─────────────────────────────

/**
 * Schéma miroir de `GroupDtoSchema` côté backend (cf.
 * packages/backend/src/routes/groups/schemas.ts → GroupDtoSchema).
 *
 * Ne pas inventer de champs : on aligne strictement sur ce que renvoie
 * `groupToDto`. À terme (J4b-bis), ces schémas vivront dans `@nexus/shared`
 * pour ne plus avoir à les redéfinir des deux côtés.
 */
const GroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  role: z.enum(['owner', 'admin', 'member']).optional(),
  /** Cf. backend `GroupDtoSchema` — présent car `useGroups` demande
   *  systématiquement `withMemberCount=true` (ticket 8a080863). */
  memberCount: z.number().int().nonnegative().optional(),
});
export type Group = z.infer<typeof GroupSchema>;

const GroupListReply = z.object({ groups: z.array(GroupSchema) });

export function useGroups() {
  // Gate sur l'auth : on attend la fin de `init()` ET la présence d'un user
  // avant de fetcher. Sinon, sur cold load d'une page publique (`/e/:slug`),
  // on partait avec un access token null → 401 silencieux → pas de refetch
  // automatique quand l'auth se complète, donc `isMember` restait toujours
  // false. La queryKey inclut l'`userId` pour invalider proprement quand on
  // change d'utilisateur (logout/relogin).
  const userId = useAuth((s) => s.user?.id);
  const initializing = useAuth((s) => s.initializing);
  return useQuery({
    enabled: !!userId && !initializing,
    queryKey: ['groups', userId ?? null],
    queryFn: async () =>
      // `withMemberCount=true` (ticket 8a080863) : évite à tout consommateur
      // de cette liste (rail desktop, liste mobile) de refaire un
      // `GET /:groupId/members` complet juste pour afficher un total.
      api({ method: 'GET', path: '/groups?withMemberCount=true', reply: GroupListReply }).then(
        (r) => r.groups,
      ),
  });
}

const GroupMemberSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  role: z.enum(['owner', 'admin', 'member']),
  joinedAt: z.string(),
});
export type GroupMember = z.infer<typeof GroupMemberSchema>;
const GroupMembersReply = z.object({ members: z.array(GroupMemberSchema) });

export function useGroupMembers(groupId: string | undefined) {
  return useQuery({
    enabled: !!groupId,
    queryKey: ['group-members', groupId],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/groups/${requireId(groupId, 'group')}/members`,
        reply: GroupMembersReply,
      }).then((r) => r.members),
  });
}

/**
 * Promeut/rétrograde un membre du groupe (MAN-180 Phase 1). Réservé aux
 * membres dont le rang est strictement supérieur à la cible côté backend
 * (`canManageRole` — 403 `PERMISSION_DENIED` sinon). Met à jour directement
 * le cache `['group-members', groupId]` avec le DTO renvoyé par la réponse
 * HTTP plutôt que d'attendre un refetch — le WS `member:role_updated`
 * (câblé dans `useKillerFeaturesWs`) réconcilie de toute façon les autres
 * onglets/utilisateurs.
 */
export function useUpdateGroupMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; userId: string; role: 'admin' | 'member' }) => {
      const reply = await api({
        method: 'PATCH',
        path: `/groups/${input.groupId}/members/${input.userId}/role`,
        body: { role: input.role },
        reply: z.object({ member: GroupMemberSchema }),
      });
      return reply.member;
    },
    onSuccess: (member, vars) => {
      qc.setQueryData<GroupMember[]>(['group-members', vars.groupId], (list) =>
        list ? list.map((m) => (m.userId === member.userId ? member : m)) : list,
      );
    },
  });
}

/**
 * Transfère la propriété du groupe (MAN-181 Phase 2). Réservé au `owner`
 * courant côté backend (403 `PERMISSION_DENIED` sinon, 400 sur auto-transfert,
 * 404 si la cible n'est pas membre). La réponse HTTP ne renvoie que
 * `{ ok: true }` — pas de DTO membre à jour — donc la mise à jour du cache
 * `['group-members', groupId]` se déduit ici : la cible passe `owner`, et
 * quiconque portait déjà ce rôle dans le cache passe `admin` (miroir client
 * de `transferOwnership` côté backend, cf.
 * `packages/backend/src/routes/groups/service.ts`).
 */
export function useTransferGroupOwnership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; newOwnerUserId: string }) => {
      await api({
        method: 'POST',
        path: `/groups/${input.groupId}/transfer-ownership`,
        body: { newOwnerUserId: input.newOwnerUserId },
        reply: z.object({ ok: z.boolean() }),
      });
      return input;
    },
    onSuccess: ({ groupId, newOwnerUserId }) => {
      qc.setQueryData<GroupMember[]>(['group-members', groupId], (list) =>
        list
          ? list.map((m) => {
              if (m.userId === newOwnerUserId) return { ...m, role: 'owner' as const };
              if (m.role === 'owner') return { ...m, role: 'admin' as const };
              return m;
            })
          : list,
      );
      // `['groups', userId]` porte le `role` du viewer par groupe (consommé
      // par `GroupsSection` en Settings, cf. MAN-192) — distinct du cache
      // `['group-members', groupId]` patché ci-dessus. Sans cette
      // invalidation, l'accordéon Settings continue d'afficher l'ancien rôle
      // du viewer après un transfert et ré-active des actions qui vont
      // désormais 403/409 côté serveur. `['groups']` est un préfixe valide
      // de la vraie clé `['groups', userId]` (même pattern que
      // `useCreateGroup`/`useDeleteGroup`/`useAcceptInvitation`).
      void qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      const reply = await api({
        method: 'POST',
        path: '/groups',
        body: input,
        reply: z.object({ group: GroupSchema }),
      });
      return reply.group;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      return api({
        method: 'POST',
        path: `/invitations/${slug}/accept`,
        body: {},
        reply: z.object({ group: GroupSchema }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

const InvitationSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  slug: z.string(),
  role: z.enum(['owner', 'admin', 'member']),
  maxUses: z.number().int().nullable(),
  usedCount: z.number().int(),
  // Colonne DB non-nullable côté backend (`invitationToDto`, `service.ts`) :
  // le backend calcule toujours une date d'expiration réelle, même sans
  // `ttlMs` explicite à la création — pas de branche "pas d'expiration" côté
  // client (MAN-198 Item 4, plus strict que le `.nullable()` d'origine).
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type InvitationDto = z.infer<typeof InvitationSchema>;

/**
 * Quota d'utilisation d'une invitation, phrasing partagé entre `InviteDialog`
 * (`GroupMenu.tsx`) et la ligne de liste `InvitationRow`
 * (`GroupInvitationsSection.tsx`, MAN-198 Item 2) — "Utilisations
 * illimitées" repris tel quel de `InviteDialog`, seule source jusqu'ici.
 */
export function formatInvitationUsage(
  invitation: Pick<InvitationDto, 'usedCount' | 'maxUses'>,
): string {
  return invitation.maxUses === null
    ? 'Utilisations illimitées'
    : `${invitation.usedCount}/${invitation.maxUses} utilisations`;
}

/**
 * Date d'expiration d'une invitation, phrasing ("expire le JJ/MM/AAAA")
 * repris de `InviteDialog` (`GroupMenu.tsx`). `expiresAt` est non-nullable
 * (cf. `InvitationSchema` ci-dessus, MAN-198 Item 4) : pas de branche "pas
 * d'expiration" à gérer ici.
 */
export function formatInvitationExpiry(invitation: Pick<InvitationDto, 'expiresAt'>): string {
  return `expire le ${new Date(invitation.expiresAt).toLocaleDateString('fr-FR')}`;
}

export interface CreateInvitationInput {
  groupId: string;
  role?: 'owner' | 'admin' | 'member';
  maxUses?: number | null;
  ttlMs?: number;
}

/**
 * Réservé aux admin+ du groupe (vérifié côté backend). Crée une invitation
 * et renvoie le DTO complet — le lien à partager est
 * `${origin}/invite/${invitation.slug}`.
 */
export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvitationInput) => {
      const { groupId, ...body } = input;
      const reply = await api({
        method: 'POST',
        path: `/groups/${groupId}/invitations`,
        body,
        reply: z.object({ invitation: InvitationSchema }),
      });
      return reply.invitation;
    },
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: ['invitations', inv.groupId] });
    },
  });
}

const InvitationListReply = z.object({ invitations: z.array(InvitationSchema) });

/**
 * Liste TOUTES les invitations du groupe — actives ET révoquées, triées du
 * plus récent au plus ancien (`listInvitationsForGroup` côté backend n'a
 * aucun filtre `revokedAt IS NULL`, cf. `packages/backend/src/routes/groups/
 * service.ts`). Filtrer aux seules invitations actives est une préoccupation
 * d'affichage (`GroupInvitationsSection`), pas de ce hook, qui reste un
 * miroir honnête de l'endpoint.
 *
 * Réservé aux admin+ du groupe côté backend (`requireGroupRole(req,
 * 'admin')` — 403 sinon) : `enabled` est donc entièrement délégué à
 * l'appelant (`viewerRole !== 'member'`), ce hook ne connaît pas lui-même le
 * rôle du viewer.
 */
export function useListInvitations(groupId: string, enabled: boolean) {
  return useQuery({
    enabled: enabled && !!groupId,
    queryKey: ['invitations', groupId],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/groups/${groupId}/invitations`,
        reply: InvitationListReply,
      }).then((r) => r.invitations),
  });
}

/**
 * Révoque une invitation (admin+ du groupe, vérifié côté backend). Le
 * endpoint ne renvoie que `{ ok: true }` : on invalide simplement le cache
 * `['invitations', groupId]` plutôt que de calculer un état local, même
 * principe que `useDeleteGroup`/`useLeaveGroup` pour les mutations dont la
 * réponse ne porte pas de DTO à jour.
 */
export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, invitationId }: { groupId: string; invitationId: string }) => {
      await api({ method: 'DELETE', path: `/groups/${groupId}/invitations/${invitationId}` });
    },
    onSuccess: (_data, { groupId }) => {
      void qc.invalidateQueries({ queryKey: ['invitations', groupId] });
    },
  });
}

/**
 * Owner-only : supprime un groupe entier (cascade côté backend sur
 * group_members, invitations, messaging_sessions, etc.).
 */
export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      await api({ method: 'DELETE', path: `/groups/${groupId}` });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

/**
 * Utilisé pour DEUX cas d'usage distincts, même endpoint `DELETE
 * /groups/:groupId/members/:userId` : le self-leave (`GroupMenu.tsx`, où
 * `userId` est celui du viewer) et le kick d'un tiers (`RemoveMemberDialog`
 * dans `GroupMembersPanel.tsx`, où `userId` est celui de la cible — le nom
 * du hook reste "leave" car c'est le même contrat serveur, seul le
 * `userId` diffère).
 *
 * `onSuccess` doit donc invalider trois caches différents selon le cas :
 *  - `['group-members', groupId]` (toujours) : la cible retirée disparaît
 *    de la liste des membres. Avant ce correctif (MAN-192, revue), seul
 *    `['groups']` était invalidé — un kick laissait ce cache intact, et le
 *    membre kické réapparaissait après collapse/re-expand de l'accordéon
 *    Settings (le state local de `GroupMembersPanel` est remonté à chaque
 *    montage, mais relit alors le cache non invalidé, souvent servi tel
 *    quel vu le `staleTime` global de 30s).
 *  - `['groups']` (seulement si `userId === currentUserId`, i.e. self-leave) :
 *    c'est la LISTE DES GROUPES DU VIEWER qui change (il n'en fait plus
 *    partie) — un kick ne change rien à la liste de groupes du viewer
 *    lui-même, invalider `['groups']` dans ce cas serait un aller-retour
 *    réseau gratuit.
 *  - `['notifications']` (self-leave seulement, cf. a001d5d2) : le serveur
 *    vient de purger mes notifications de ce groupe ; sans invalidation la
 *    cloche les resservirait depuis le cache, liens vers un groupe
 *    inaccessible compris. Le kické, lui, reçoit un `notification:created`
 *    (`member_removed`) qui invalide déjà la cloche.
 */
export function useLeaveGroup() {
  const qc = useQueryClient();
  const currentUserId = useAuth((s) => s.user?.id);
  return useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      await api({ method: 'DELETE', path: `/groups/${groupId}/members/${userId}` });
    },
    onSuccess: (_data, { groupId, userId }) => {
      qc.setQueryData<GroupMember[]>(['group-members', groupId], (list) =>
        list ? list.filter((m) => m.userId !== userId) : list,
      );
      if (userId === currentUserId) {
        void qc.invalidateQueries({ queryKey: ['groups'] });
        // Le serveur vient de purger mes notifications de ce groupe (cf.
        // a001d5d2) : sans ça, la cloche les resservirait depuis le cache,
        // liens vers un groupe inaccessible compris. Self-leave seulement —
        // le kické, lui, est prévenu par le `notification:created` de son
        // `member_removed`, qui invalide déjà la cloche.
        void qc.invalidateQueries({ queryKey: ['notifications'] });
      }
    },
  });
}

// ─────────────────────────── Messaging ────────────────────────────

/**
 * Schéma miroir du DTO `ProviderSessionView` côté backend (cf.
 * packages/backend/src/integrations/core/session-store.ts → sessionToView).
 *
 * IMPORTANT : `status` matche `provider_session_status` Postgres :
 *   connecting | connected | disconnected | error
 *
 * Ne pas inventer d'autres valeurs — un mismatch Zod fait échouer la
 * validation de la réponse et la liste apparaît vide côté UI sans
 * message d'erreur visible (cf. dette tracée dans backlog J4b-bis).
 */
const MessagingSessionStatusSchema = z.enum(['connecting', 'connected', 'disconnected', 'error']);
export type MessagingSessionStatus = z.infer<typeof MessagingSessionStatusSchema>;

const MessagingSessionSchema = z.object({
  id: z.string().uuid(),
  // M1 (post-ADR-027) : sessions scopées USER. Plus de groupId.
  userId: z.string().uuid(),
  // ADR-027 : universalisation webview messaging — l'enum DB inclut désormais
  // 9 providers supplémentaires (telegram → snapchat) en plus des 3 historiques.
  // Le frontend les surface tous en webview encapsulé (cf. PROVIDER_WEB_URL).
  providerType: z.enum([
    'discord',
    'whatsapp',
    'messenger',
    'telegram',
    'instagram',
    'slack',
    'teams',
    'linkedin',
    'twitter',
    'reddit',
    'tiktok',
    'snapchat',
  ]),
  externalId: z.string(),
  displayName: z.string(),
  status: MessagingSessionStatusSchema,
  statusDetail: z.string().nullable(),
  lastConnectedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MessagingSession = z.infer<typeof MessagingSessionSchema>;
const MessagingSessionsReply = z.object({ sessions: z.array(MessagingSessionSchema) });

/**
 * Liste les sessions messagerie de l'utilisateur courant.
 *
 * Depuis M1 (post-ADR-027) : sessions scopées USER (pas GROUP). Un user a
 * son compte WhatsApp / Discord / etc. INDÉPENDAMMENT des groupes nexus
 * auxquels il appartient.
 *
 * Gaté sur l'auth : `AppShell` appelle ce hook AU-DESSUS de son garde
 * `if (!user)`, donc sans gate il partait en fetch sur `/me/...` avec un
 * token nul — 401 silencieux, et un observer encore vivant au moment où
 * `useResetCacheOnUserChange` vide le cache (cf. 10bc1096).
 */
export function useMessagingSessions() {
  const userId = useAuth((s) => s.user?.id);
  const initializing = useAuth((s) => s.initializing);
  return useQuery({
    enabled: !!userId && !initializing,
    queryKey: ['me-messaging-sessions'],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/me/messaging/sessions`,
        reply: MessagingSessionsReply,
      }).then((r) => r.sessions),
  });
}

/**
 * Déconnecte une session messagerie : hard-delete la ligne en base, puis
 * détruit la webview Tauri associée (`destroyProviderWebview`) — elle n'est
 * PAS laissée ouverte. Le compte provider lui-même (Discord/WhatsApp/etc.)
 * n'est pas déconnecté : seule la session nexus disparaît, la partition
 * disque (`data_directory`) est volontairement conservée pour éviter une
 * ré-authentification à la prochaine connexion (cf. `providerWebviewLabel`).
 *
 * MAN-238 : le label webview (donc le `data_directory` Tauri) est dérivé de
 * `userId`, pas de `session.id` — `sessions.id` est un `uuid().defaultRandom()`
 * qui change à chaque reconnexion (hard delete + insert), alors que `userId`
 * est l'identité stable garantie unique côté backend
 * (`externalId = 'webview:${userId}'`). Reconnecter le même provider réutilise
 * donc la même partition webview : cookies préservés, pas de nouvelle
 * ré-authentification.
 *
 * Extrait en fonction libre (plutôt que gardé inline dans
 * `useDeleteMessagingSession`) pour MAN-239 Phase 2 : `useDeleteProviderLocalData`
 * la réutilise telle quelle pour déconnecter AVANT de purger la partition
 * quand le provider est encore connecté au moment de la purge.
 *
 * Invalide le cache `me-messaging-sessions` pour que l'UI repasse à
 * "Non connecté" sans refresh. On attend le destroy avant d'invalider le
 * cache pour éviter qu'un destroy tardif ferme une webview fraîchement
 * recréée par une reconnexion rapide (même raisonnement que MAN-238).
 */
async function disconnectMessagingSession(
  qc: QueryClient,
  {
    sessionId,
    providerType,
    userId,
  }: {
    sessionId: string;
    providerType: WebviewProvider;
    userId: string;
  },
): Promise<void> {
  await api({
    method: 'DELETE',
    path: `/me/messaging/sessions/${sessionId}`,
    reply: z.object({ ok: z.literal(true) }),
  });
  const label = providerWebviewLabel(providerType, userId);
  await destroyProviderWebview(label).catch((err) => {
    console.warn('[delete-session] destroyProviderWebview failed', err);
  });
  void qc.invalidateQueries({ queryKey: ['me-messaging-sessions'] });
}

/**
 * Supprime une session messagerie (déconnexion explicite depuis Réglages
 * Connexions). Wrapper `useMutation` autour de `disconnectMessagingSession`
 * — cf. sa JSDoc pour le détail du comportement (hard delete + destroy
 * webview + invalidation cache).
 */
export function useDeleteMessagingSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      sessionId: string;
      // Polish P3 : requis pour recalculer le label webview et cleanup la
      // partition Tauri persistante associée (cf. P3 backlog). En mode web
      // pur (non-Tauri), destroyProviderWebview est un no-op — les deux
      // champs sont quand même requis pour éviter un skip silencieux si un
      // futur appelant oublie l'un des deux (2 call sites aujourd'hui,
      // tous deux les passent déjà).
      providerType: WebviewProvider;
      userId: string;
    }) => {
      await disconnectMessagingSession(qc, vars);
    },
  });
}

/**
 * Supprime les données locales persistées d'un provider webview (MAN-239).
 * Purge le `data_directory` Tauri (cookies, cache, storage) — action de
 * nettoyage explicite (équivalent "logout" / RGPD), distincte de la session
 * nexus en base.
 *
 * Phase 1 : chemin direct, purement Tauri, aucun appel backend (rien côté
 * serveur à représenter "ce provider a des données locales" — pas de DTO,
 * pas de cache TanStack Query associé).
 *
 * Phase 2 (MAN-239) : si le provider est encore connecté au moment de la
 * purge (`session.status === 'connected'`), la mutation déconnecte D'ABORD
 * la session nexus (même effet que `useDeleteMessagingSession` — réutilise
 * `disconnectMessagingSession`) avant de purger la partition. Purger sous
 * une webview encore montée laisserait une webview ouverte sur un
 * `data_directory` supprimé, un état incohérent côté desktop. Si la
 * déconnexion échoue, la purge n'a PAS lieu : l'erreur remonte telle quelle
 * à l'appelant, pas de retry silencieux ni d'état ambigu — c'est la garantie
 * explicitement requise par MAN-239 Phase 2 (pas de purge sans déconnexion
 * confirmée). L'appelant omettant `session` (ou passant un statut non
 * `connected`) retrouve exactement le comportement Phase 1 : purge directe,
 * aucun appel réseau.
 *
 * `mutationFn` compose les deux étapes séquentiellement dans le MÊME appel
 * TanStack Query : `isPending` couvre donc la durée totale de l'opération
 * (déconnexion + purge), pas seulement la première étape — l'UI n'a rien de
 * spécial à faire pour désactiver son bouton pendant les deux étapes.
 *
 * `deleteProviderWebviewData` est idempotent côté Rust (dossier déjà absent
 * = succès) — pas de garde `enabled`/pré-check ici, un appelant peut
 * toujours retenter sans effet de bord.
 */
export function useDeleteProviderLocalData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      providerType,
      userId,
      session,
    }: {
      providerType: WebviewProvider;
      userId: string;
      /**
       * Session nexus courante du provider, si connue de l'appelant — la
       * détection de "provider encore connecté" (déclenche le disconnect
       * préalable) repose sur `session?.status === 'connected'`. Absente
       * (ou `status !== 'connected'`) → chemin direct Phase 1 inchangé.
       */
      session?: { id: string; status: MessagingSessionStatus };
    }) => {
      if (session?.status === 'connected') {
        await disconnectMessagingSession(qc, { sessionId: session.id, providerType, userId });
      }
      const label = providerWebviewLabel(providerType, userId);
      await deleteProviderWebviewData(label);
      return { label };
    },
  });
}

// Polish P4 (révision) : le hook `useReorderMessagingSessions` a été retiré.
// L'ordre des sessions est purement client-side (par-user) via localStorage,
// géré directement dans `AppShell.tsx` (helpers `readSessionOrder` /
// `writeSessionOrder`). Pas de mutation API nécessaire.

/**
 * Connecte un provider messagerie en mode "webview encapsulée" (cf.
 * ADR-022/025/027). Depuis M1, scope USER : un user a son compte
 * WhatsApp / Discord / etc. INDÉPENDAMMENT des groupes.
 *
 * Pas de credentials transitant côté backend, pas d'OAuth flow. La route
 * POST crée juste une session "déclarative" qui permet au front d'afficher
 * le panneau webview correspondant. L'auth se fait dans la webview elle-même
 * (QR code WA, login Messenger, etc.).
 *
 * Idempotent côté backend : appeler 2x avec le même provider renvoie la
 * même session.
 */
export function useConnectWebviewProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      providerType,
    }: {
      // ADR-027 : 12 providers webview-encapsulés (Discord inclus).
      providerType:
        | 'discord'
        | 'whatsapp'
        | 'messenger'
        | 'telegram'
        | 'instagram'
        | 'slack'
        | 'teams'
        | 'linkedin'
        | 'twitter'
        | 'reddit'
        | 'tiktok'
        | 'snapchat';
    }) => {
      const reply = await api({
        method: 'POST',
        path: `/me/messaging/webview-sessions`,
        body: { providerType },
        reply: z.object({ session: MessagingSessionSchema }),
      });
      return reply.session;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-messaging-sessions'] });
    },
  });
}

// ADR-027 : suppression des hooks `useDiscordInstallUrl`, `useChannels`,
// `useMessages`, `useSendMessage`. Toutes les messageries sont désormais
// webview-encapsulées (pas de channels Nexus, pas d'API messages serveur).

// ─────────────────── Events (J5b #38 — branche DB) ───────────────────
//
// Schéma miroir du DTO backend (cf. packages/backend/src/routes/events/
// schemas.ts → EventDtoSchema). À déplacer en @nexus/shared en J4b-bis.

const RsvpValueSchema = z.enum(['yes', 'maybe', 'no']);
export type RsvpValue = z.infer<typeof RsvpValueSchema>;

const EventRsvpSchema = z.object({
  userId: z.string().uuid(),
  value: RsvpValueSchema,
});

const EventSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  groupId: z.string().uuid(),
  tags: z.array(z.string()),
  title: z.string(),
  description: z.string().nullable(),
  startsAt: z.string(),
  location: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  rsvps: z.array(EventRsvpSchema),
});
export type EventDto = z.infer<typeof EventSchema>;

const EventListReply = z.object({ events: z.array(EventSchema) });
const EventReply = z.object({ event: EventSchema });

export interface ListEventsFilter {
  when?: 'upcoming' | 'past' | 'all';
}

export function useEvents(groupId: string | undefined, filter: ListEventsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.when) params.set('when', filter.when);
  const qs = params.toString();
  return useQuery({
    enabled: !!groupId,
    queryKey: ['events', groupId, filter],
    queryFn: () =>
      api({
        method: 'GET',
        path: `/groups/${requireId(groupId, 'group')}/events${qs ? `?${qs}` : ''}`,
        reply: EventListReply,
      }).then((r) => r.events),
  });
}

export function useEvent(eventId: string | undefined) {
  return useQuery({
    enabled: !!eventId,
    queryKey: ['event', eventId],
    queryFn: () =>
      api({
        method: 'GET',
        path: `/events/${requireId(eventId, 'event')}`,
        reply: EventReply,
      }).then((r) => r.event),
  });
}

export interface CreateEventInput {
  groupId: string;
  tags?: string[];
  title: string;
  description?: string | null;
  startsAt: string; // ISO
  location?: string | null;
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEventInput) => {
      const { groupId, ...body } = input;
      const reply = await api({
        method: 'POST',
        path: `/groups/${groupId}/events`,
        body,
        reply: EventReply,
      });
      return reply.event;
    },
    onSuccess: (event) => {
      void qc.invalidateQueries({ queryKey: ['events', event.groupId] });
    },
  });
}

export interface UpdateEventInput {
  eventId: string;
  tags?: string[];
  title?: string;
  description?: string | null;
  startsAt?: string;
  location?: string | null;
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateEventInput) => {
      const { eventId, ...body } = input;
      const reply = await api({
        method: 'PATCH',
        path: `/events/${eventId}`,
        body,
        reply: EventReply,
      });
      return reply.event;
    },
    onSuccess: (event) => {
      void qc.invalidateQueries({ queryKey: ['events', event.groupId] });
      void qc.invalidateQueries({ queryKey: ['event', event.id] });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId }: { eventId: string; groupId: string }) => {
      await api({
        method: 'DELETE',
        path: `/events/${eventId}`,
        reply: z.object({ ok: z.literal(true) }),
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['events', vars.groupId] });
      void qc.removeQueries({ queryKey: ['event', vars.eventId] });
    },
  });
}

export function useEventRsvp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventId: string; value: RsvpValue | null }) => {
      const reply = await api({
        method: 'POST',
        path: `/events/${input.eventId}/rsvp`,
        body: { value: input.value },
        reply: EventReply,
      });
      return reply.event;
    },
    // ─── Optimistic update (cf. backlog J4b-bis) ───────────────────────
    // Le user clique sur Yes/Maybe/No → on patche immédiatement le cache
    // local pour que la donut RSVP, le badge "Mes réponses en attente"
    // et la card de l'event reflètent le choix instantanément. Si le
    // backend KO, onError rollback. onSettled refetch pour réconcilier.
    onMutate: async (input) => {
      const userId = useAuth.getState().user?.id;
      if (!userId) return { snapshots: [] };

      // Lire l'event en cache pour récupérer son groupId (besoin pour
      // cibler les queries `['events', groupId, filter]`).
      const eventKey = ['event', input.eventId] as const;
      const cachedEvent = qc.getQueryData<EventDto>(eventKey);
      const groupId = cachedEvent?.groupId;

      // Cancel les queries en vol pour ne pas écraser notre optimistic.
      await qc.cancelQueries({ queryKey: eventKey });
      if (groupId) {
        await qc.cancelQueries({ queryKey: ['events', groupId] });
      }
      if (cachedEvent?.slug) {
        await qc.cancelQueries({ queryKey: ['public-event', cachedEvent.slug] });
      }

      // Snapshot pour rollback.
      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      if (cachedEvent) snapshots.push({ key: [...eventKey], data: cachedEvent });
      if (groupId) {
        qc.getQueriesData<EventDto[]>({ queryKey: ['events', groupId] }).forEach(([k, d]) => {
          if (d) snapshots.push({ key: k, data: d });
        });
      }
      if (cachedEvent?.slug) {
        const publicKey = ['public-event', cachedEvent.slug] as const;
        const publicCached = qc.getQueryData<EventDto>(publicKey);
        if (publicCached) {
          snapshots.push({ key: [...publicKey], data: publicCached });
        }
      }

      // Patch helper : remplace le RSVP du user dans event.rsvps.
      const patchEvent = (e: EventDto): EventDto => {
        if (e.id !== input.eventId) return e;
        const without = e.rsvps.filter((r) => r.userId !== userId);
        const next = input.value ? [...without, { userId, value: input.value }] : without;
        return { ...e, rsvps: next };
      };

      if (cachedEvent) {
        qc.setQueryData<EventDto>(eventKey, patchEvent(cachedEvent));
      }
      if (groupId) {
        qc.setQueriesData<EventDto[]>({ queryKey: ['events', groupId] }, (list) =>
          list ? list.map(patchEvent) : list,
        );
      }
      if (cachedEvent?.slug) {
        qc.setQueriesData<EventDto>({ queryKey: ['public-event', cachedEvent.slug] }, (e) =>
          e ? patchEvent(e) : e,
        );
      }

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (event, _err, input) => {
      // Réconciliation : on refetch contre la source de vérité serveur.
      // Si la mutation a réussi, on a `event` ; sinon on tente d'invalider
      // depuis l'input (le minimum requis).
      const eventId = event?.id ?? input.eventId;
      void qc.invalidateQueries({ queryKey: ['event', eventId] });
      if (event) {
        void qc.invalidateQueries({ queryKey: ['events', event.groupId] });
        // Page publique du même slug ouverte par le même user dans un autre
        // onglet → on rafraîchit aussi (le WS le fera de toute façon, mais
        // ça évite un flash dans le tab d'origine).
        void qc.invalidateQueries({ queryKey: ['public-event', event.slug] });
      }
    },
  });
}

// ─────────────────── Polls (J5b #39 — branche DB) ────────────────────

const PollOptionSchema = z.object({
  id: z.string().uuid(),
  pollId: z.string().uuid(),
  label: z.string(),
  position: z.number().int(),
  voters: z.array(z.string().uuid()),
});

const PollSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  groupId: z.string().uuid(),
  tags: z.array(z.string()),
  question: z.string(),
  multi: z.boolean(),
  closesAt: z.string().nullable(),
  options: z.array(PollOptionSchema),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PollDto = z.infer<typeof PollSchema>;

const PollListReply = z.object({ polls: z.array(PollSchema) });
const PollReply = z.object({ poll: PollSchema });

export interface ListPollsFilter {
  state?: 'open' | 'closed' | 'all';
}

export function usePolls(groupId: string | undefined, filter: ListPollsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.state) params.set('state', filter.state);
  const qs = params.toString();
  return useQuery({
    enabled: !!groupId,
    queryKey: ['polls', groupId, filter],
    queryFn: () =>
      api({
        method: 'GET',
        path: `/groups/${requireId(groupId, 'group')}/polls${qs ? `?${qs}` : ''}`,
        reply: PollListReply,
      }).then((r) => r.polls),
  });
}

export interface CreatePollInput {
  groupId: string;
  question: string;
  options: string[];
  multi?: boolean;
  closesAt?: string | null;
  tags?: string[];
}

export function useCreatePoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePollInput) => {
      const { groupId, ...body } = input;
      const reply = await api({
        method: 'POST',
        path: `/groups/${groupId}/polls`,
        body,
        reply: PollReply,
      });
      return reply.poll;
    },
    onSuccess: (poll) => {
      void qc.invalidateQueries({ queryKey: ['polls', poll.groupId] });
    },
  });
}

export function useDeletePoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pollId }: { pollId: string; groupId: string }) => {
      await api({
        method: 'DELETE',
        path: `/polls/${pollId}`,
        reply: z.object({ ok: z.literal(true) }),
      });
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['polls', vars.groupId] });
    },
  });
}

export function useVote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { pollId: string; optionId: string; value: boolean }) => {
      const reply = await api({
        method: 'POST',
        path: `/polls/${input.pollId}/vote`,
        body: { optionId: input.optionId, value: input.value },
        reply: PollReply,
      });
      return reply.poll;
    },
    // ─── Optimistic update ───────────────────────────────────────────────
    // Toggle l'userId dans option.voters de l'option ciblée. Si poll.multi=
    // false, on retire le user des autres options (un seul vote).
    onMutate: async (input) => {
      const userId = useAuth.getState().user?.id;
      if (!userId) return { snapshots: [] };

      // Trouver le poll dans le cache pour récupérer groupId + multi + slug.
      const allPolls = qc.getQueriesData<PollDto[]>({ queryKey: ['polls'] });
      let target: PollDto | undefined;
      for (const [, list] of allPolls) {
        const found = list?.find((p) => p.id === input.pollId);
        if (found) {
          target = found;
          break;
        }
      }
      if (!target) return { snapshots: [] };

      const groupId = target.groupId;
      const slug = target.slug;
      const multi = target.multi;

      await qc.cancelQueries({ queryKey: ['polls', groupId] });
      if (slug) await qc.cancelQueries({ queryKey: ['public-poll', slug] });

      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      qc.getQueriesData<PollDto[]>({ queryKey: ['polls', groupId] }).forEach(([k, d]) => {
        if (d) snapshots.push({ key: k, data: d });
      });
      const publicCached = qc.getQueryData<PollDto>(['public-poll', slug]);
      if (publicCached) snapshots.push({ key: ['public-poll', slug], data: publicCached });

      const patchPoll = (p: PollDto): PollDto => {
        if (p.id !== input.pollId) return p;
        return {
          ...p,
          options: p.options.map((opt) => {
            if (opt.id === input.optionId) {
              const without = opt.voters.filter((v) => v !== userId);
              return {
                ...opt,
                voters: input.value ? [...without, userId] : without,
              };
            }
            // Single-choice : retirer le user des autres options s'il vient
            // d'ajouter une voix ailleurs.
            if (!multi && input.value) {
              return { ...opt, voters: opt.voters.filter((v) => v !== userId) };
            }
            return opt;
          }),
        };
      };

      qc.setQueriesData<PollDto[]>({ queryKey: ['polls', groupId] }, (list) =>
        list ? list.map(patchPoll) : list,
      );
      if (publicCached) {
        qc.setQueryData<PollDto>(['public-poll', slug], patchPoll(publicCached));
      }

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (poll, _err, input) => {
      if (poll) {
        void qc.invalidateQueries({ queryKey: ['polls', poll.groupId] });
        void qc.invalidateQueries({ queryKey: ['public-poll', poll.slug] });
      } else {
        // Best-effort si KO : tente d'invalider via tous les groupes en cache.
        void qc.invalidateQueries({ queryKey: ['polls'] });
        void qc.invalidateQueries({ queryKey: ['public-poll'] });
      }
      void input; // unused but kept for signature consistency
    },
  });
}

// ───────────────────────────── Expenses ─────────────────────────────
//
// Aligné sur `packages/backend/src/routes/expenses/schemas.ts → ExpenseDtoSchema`
// (J5b #40). Les balances ne sont PAS renvoyées par le backend : on les
// calcule côté front depuis la liste des expenses (pas de besoin
// d'invalidation séparée → recalcul gratuit à chaque mutation).

const ExpenseShareSchema = z.object({
  expenseId: z.string().uuid(),
  userId: z.string().uuid(),
  shareCents: z.number().int().nonnegative(),
  isSettled: z.boolean(),
  settledAt: z.string().nullable(),
  /**
   * Nom du porteur de la part, résolu par le backend (cf. 10af5c92).
   *
   * Optionnel des deux côtés : absent des lectures publiques, où seuls des
   * fragments d'identifiant sont exposés. Sans cette ligne, Zod stripperait
   * le champ à la validation de la réponse et l'écran ne le verrait jamais.
   */
  userName: z.string().optional(),
});
export type ExpenseShareDto = z.infer<typeof ExpenseShareSchema>;

const ExpenseSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  groupId: z.string().uuid(),
  tags: z.array(z.string()),
  description: z.string(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  paidBy: z.string().uuid(),
  /** Nom du payeur. Mêmes règles que `ExpenseShareSchema.userName`. */
  paidByName: z.string().optional(),
  settledAt: z.string().nullable(),
  shares: z.array(ExpenseShareSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExpenseDto = z.infer<typeof ExpenseSchema>;

const ExpenseListReply = z.object({ expenses: z.array(ExpenseSchema) });
const ExpenseReply = z.object({ expense: ExpenseSchema });

export function useExpenses(
  groupId: string | undefined,
  filter?: { state?: 'open' | 'settled' | 'all' },
) {
  const params = new URLSearchParams();
  if (filter?.state) params.set('state', filter.state);
  const qs = params.toString();
  return useQuery({
    enabled: !!groupId,
    queryKey: ['expenses', groupId, filter?.state ?? 'all'],
    queryFn: async () => {
      const path = `/groups/${requireId(groupId, 'group')}/expenses${qs ? `?${qs}` : ''}`;
      const reply = await api({ method: 'GET', path, reply: ExpenseListReply });
      return reply.expenses;
    },
  });
}

export function useExpense(expenseId: string | undefined) {
  return useQuery({
    enabled: !!expenseId,
    queryKey: ['expense', expenseId],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/expenses/${requireId(expenseId, 'expense')}`,
        reply: ExpenseReply,
      }).then((r) => r.expense),
  });
}

export interface CreateExpenseInput {
  groupId: string;
  tags?: string[];
  description: string;
  amountCents: number;
  currency?: string;
  paidBy: string;
  shares: { userId: string; shareCents: number }[];
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExpenseInput) => {
      const { groupId, ...body } = input;
      const reply = await api({
        method: 'POST',
        path: `/groups/${groupId}/expenses`,
        body: { currency: 'EUR', ...body },
        reply: ExpenseReply,
      });
      return reply.expense;
    },
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['expenses', e.groupId] });
    },
  });
}

export interface UpdateExpenseInput {
  expenseId: string;
  tags?: string[];
  description?: string;
  amountCents?: number;
  currency?: string;
  paidBy?: string;
  shares?: { userId: string; shareCents: number }[];
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateExpenseInput) => {
      const { expenseId, ...body } = input;
      const reply = await api({
        method: 'PATCH',
        path: `/expenses/${expenseId}`,
        body,
        reply: ExpenseReply,
      });
      return reply.expense;
    },
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['expense', e.id] });
      void qc.invalidateQueries({ queryKey: ['expenses', e.groupId] });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { expenseId: string; groupId: string }) => {
      await api({
        method: 'DELETE',
        path: `/expenses/${input.expenseId}`,
        reply: z.object({ ok: z.literal(true) }),
      });
      return input;
    },
    onSuccess: (input) => {
      void qc.invalidateQueries({ queryKey: ['expenses', input.groupId] });
      void qc.invalidateQueries({ queryKey: ['expense', input.expenseId] });
    },
  });
}

/**
 * Marque (ou démarque) MA part d'une expense comme réglée.
 * `userId` n'est pas pris en paramètre côté API — c'est toujours l'user
 * authentifié. Le hook expose seulement `expenseId` + `settled`.
 */
export function useSettleExpenseShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { expenseId: string; settled: boolean }) => {
      const reply = await api({
        method: 'POST',
        path: `/expenses/${input.expenseId}/settle`,
        body: { settled: input.settled },
        reply: ExpenseReply,
      });
      return reply.expense;
    },
    // ─── Optimistic update ───────────────────────────────────────────────
    // Toggle isSettled de la share du user courant. Met aussi à jour
    // `settledAt` pour cohérence visuelle (badge "Réglée le X").
    onMutate: async (input) => {
      const userId = useAuth.getState().user?.id;
      if (!userId) return { snapshots: [] };

      const expenseKey = ['expense', input.expenseId] as const;
      const cachedExpense = qc.getQueryData<ExpenseDto>(expenseKey);
      const groupId = cachedExpense?.groupId;

      await qc.cancelQueries({ queryKey: expenseKey });
      if (groupId) await qc.cancelQueries({ queryKey: ['expenses', groupId] });

      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      if (cachedExpense) snapshots.push({ key: [...expenseKey], data: cachedExpense });
      if (groupId) {
        qc.getQueriesData<ExpenseDto[]>({ queryKey: ['expenses', groupId] }).forEach(([k, d]) => {
          if (d) snapshots.push({ key: k, data: d });
        });
      }

      const nowIso = new Date().toISOString();
      const patchExpense = (e: ExpenseDto): ExpenseDto => {
        if (e.id !== input.expenseId) return e;
        return {
          ...e,
          shares: e.shares.map((s) =>
            s.userId === userId
              ? {
                  ...s,
                  isSettled: input.settled,
                  settledAt: input.settled ? nowIso : null,
                }
              : s,
          ),
        };
      };

      if (cachedExpense) {
        qc.setQueryData<ExpenseDto>(expenseKey, patchExpense(cachedExpense));
      }
      if (groupId) {
        qc.setQueriesData<ExpenseDto[]>({ queryKey: ['expenses', groupId] }, (list) =>
          list ? list.map(patchExpense) : list,
        );
      }

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (e, _err, input) => {
      const expenseId = e?.id ?? input.expenseId;
      void qc.invalidateQueries({ queryKey: ['expense', expenseId] });
      if (e) void qc.invalidateQueries({ queryKey: ['expenses', e.groupId] });
      // La page publique peut être ouverte côté user authentifié → on
      // invalide aussi le cache public (cf. useKillerFeaturesWs).
      void qc.invalidateQueries({ queryKey: ['public-expense'] });
    },
  });
}

/**
 * Calcule la balance nette de chaque user à partir d'une liste d'expenses.
 *  - balance > 0 → on lui doit (il a payé plus que sa part)
 *  - balance < 0 → il doit (il a moins payé que sa part)
 *
 * On ignore les shares déjà settled (l'argent a déjà été transféré). Le
 * payeur reste crédité du total (l'argent qu'il a sorti) tant que toutes
 * les shares ne sont pas settled.
 */
export function computeBalances(expenses: ExpenseDto[]): Map<string, number> {
  const balances = new Map<string, number>();
  for (const e of expenses) {
    if (e.settledAt) continue; // Expense entièrement réglée — neutralisée.
    // Le payeur a sorti `amountCents` mais a une part `myShare` (peut être 0).
    // On ne crédite que la portion encore due par les autres : la dette
    // restante = sum(shares non settled, sauf paidBy).
    let creditDuePayer = 0;
    for (const s of e.shares) {
      if (s.userId === e.paidBy) continue;
      if (s.isSettled) continue;
      creditDuePayer += s.shareCents;
      const cur = balances.get(s.userId) ?? 0;
      balances.set(s.userId, cur - s.shareCents);
    }
    if (creditDuePayer > 0) {
      const cur = balances.get(e.paidBy) ?? 0;
      balances.set(e.paidBy, cur + creditDuePayer);
    }
  }
  return balances;
}

/**
 * Récolte les noms d'affichage portés par les dépenses elles-mêmes.
 *
 * Complète la liste des membres du groupe, qui ne suffit plus : une part
 * survit au départ de son porteur, donc les écrans de dépenses doivent savoir
 * nommer quelqu'un qui n'est plus membre (cf. 10af5c92).
 */
export function collectParticipantNames(expenses: ExpenseDto[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const e of expenses) {
    if (e.paidByName) names.set(e.paidBy, e.paidByName);
    for (const s of e.shares) {
      if (s.userName) names.set(s.userId, s.userName);
    }
  }
  return names;
}

// ───────────────────────────── Todos ─────────────────────────────────
//
// Aligné sur `packages/backend/src/routes/todos/schemas.ts` (J5b #41).

const TodoItemSchema = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid(),
  text: z.string(),
  done: z.boolean(),
  assigneeId: z.string().uuid().nullable(),
  position: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TodoItemDto = z.infer<typeof TodoItemSchema>;

const TodoListSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  groupId: z.string().uuid(),
  tags: z.array(z.string()),
  title: z.string(),
  items: z.array(TodoItemSchema),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TodoListDto = z.infer<typeof TodoListSchema>;

const TodoListListReply = z.object({ todoLists: z.array(TodoListSchema) });
const TodoListReply = z.object({ todoList: TodoListSchema });
const TodoItemReply = z.object({ todoItem: TodoItemSchema });

export function useTodoLists(groupId: string | undefined) {
  return useQuery({
    enabled: !!groupId,
    queryKey: ['todos', groupId],
    queryFn: async () => {
      const reply = await api({
        method: 'GET',
        path: `/groups/${requireId(groupId, 'group')}/todo-lists`,
        reply: TodoListListReply,
      });
      return reply.todoLists;
    },
  });
}

export function useTodoList(listId: string | undefined) {
  return useQuery({
    enabled: !!listId,
    queryKey: ['todo-list', listId],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/todo-lists/${requireId(listId, 'todo list')}`,
        reply: TodoListReply,
      }).then((r) => r.todoList),
  });
}

export interface CreateTodoListInput {
  groupId: string;
  tags?: string[];
  title: string;
  initialItems?: { text: string; assigneeId?: string | null }[];
}

export function useCreateTodoList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTodoListInput) => {
      const { groupId, ...body } = input;
      const reply = await api({
        method: 'POST',
        path: `/groups/${groupId}/todo-lists`,
        body,
        reply: TodoListReply,
      });
      return reply.todoList;
    },
    onSuccess: (l) => {
      void qc.invalidateQueries({ queryKey: ['todos', l.groupId] });
    },
  });
}

export function useUpdateTodoList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string; title?: string; tags?: string[] }) => {
      const { listId, ...body } = input;
      const reply = await api({
        method: 'PATCH',
        path: `/todo-lists/${listId}`,
        body,
        reply: TodoListReply,
      });
      return reply.todoList;
    },
    onSuccess: (l) => {
      void qc.invalidateQueries({ queryKey: ['todo-list', l.id] });
      void qc.invalidateQueries({ queryKey: ['todos', l.groupId] });
    },
  });
}

export function useDeleteTodoList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string; groupId: string }) => {
      await api({
        method: 'DELETE',
        path: `/todo-lists/${input.listId}`,
        reply: z.object({ ok: z.literal(true) }),
      });
      return input;
    },
    onSuccess: (input) => {
      void qc.invalidateQueries({ queryKey: ['todos', input.groupId] });
      void qc.invalidateQueries({ queryKey: ['todo-list', input.listId] });
    },
  });
}

export function useAddTodoItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      listId: string;
      groupId: string;
      text: string;
      assigneeId?: string | null;
    }) => {
      const reply = await api({
        method: 'POST',
        path: `/todo-lists/${input.listId}/items`,
        body: { text: input.text, assigneeId: input.assigneeId ?? null },
        reply: TodoItemReply,
      });
      return { item: reply.todoItem, groupId: input.groupId };
    },
    onSuccess: ({ item, groupId }) => {
      void qc.invalidateQueries({ queryKey: ['todo-list', item.listId] });
      void qc.invalidateQueries({ queryKey: ['todos', groupId] });
      void qc.invalidateQueries({ queryKey: ['public-todo'] });
    },
  });
}

export function useUpdateTodoItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      listId: string;
      groupId: string;
      text?: string;
      done?: boolean;
      assigneeId?: string | null;
      position?: number;
    }) => {
      const { itemId, listId, groupId, ...body } = input;
      const reply = await api({
        method: 'PATCH',
        path: `/todo-items/${itemId}`,
        body,
        reply: TodoItemReply,
      });
      return { item: reply.todoItem, listId, groupId };
    },
    // ─── Optimistic update ───────────────────────────────────────────────
    // Critique pour le toggle done : cocher une case doit être instantané
    // sans flicker. On patche `text`, `done`, `assigneeId`, `position` au
    // passage (tout ce qui peut être passé en input).
    onMutate: async (input) => {
      const { itemId, listId, groupId, ...patch } = input;

      await qc.cancelQueries({ queryKey: ['todo-list', listId] });
      await qc.cancelQueries({ queryKey: ['todos', groupId] });

      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      const cachedList = qc.getQueryData<TodoListDto>(['todo-list', listId]);
      if (cachedList) snapshots.push({ key: ['todo-list', listId], data: cachedList });
      qc.getQueriesData<TodoListDto[]>({ queryKey: ['todos', groupId] }).forEach(([k, d]) => {
        if (d) snapshots.push({ key: k, data: d });
      });

      const patchItem = (i: TodoItemDto): TodoItemDto => {
        if (i.id !== itemId) return i;
        return {
          ...i,
          ...(patch.text !== undefined ? { text: patch.text } : {}),
          ...(patch.done !== undefined ? { done: patch.done } : {}),
          ...(patch.assigneeId !== undefined ? { assigneeId: patch.assigneeId } : {}),
          ...(patch.position !== undefined ? { position: patch.position } : {}),
        };
      };
      const patchList = (l: TodoListDto): TodoListDto =>
        l.id === listId ? { ...l, items: l.items.map(patchItem) } : l;

      if (cachedList) qc.setQueryData<TodoListDto>(['todo-list', listId], patchList(cachedList));
      qc.setQueriesData<TodoListDto[]>({ queryKey: ['todos', groupId] }, (list) =>
        list ? list.map(patchList) : list,
      );

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (result, _err, input) => {
      const listId = result?.listId ?? input.listId;
      const groupId = result?.groupId ?? input.groupId;
      void qc.invalidateQueries({ queryKey: ['todo-list', listId] });
      void qc.invalidateQueries({ queryKey: ['todos', groupId] });
      void qc.invalidateQueries({ queryKey: ['public-todo'] });
    },
  });
}

export function useDeleteTodoItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemId: string; listId: string; groupId: string }) => {
      await api({
        method: 'DELETE',
        path: `/todo-items/${input.itemId}`,
        reply: z.object({ ok: z.literal(true) }),
      });
      return input;
    },
    // ─── Optimistic update ───────────────────────────────────────────────
    // Suppression inline : l'item disparaît immédiatement de la liste.
    // En cas d'erreur backend, on le ré-injecte via le snapshot.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['todo-list', input.listId] });
      await qc.cancelQueries({ queryKey: ['todos', input.groupId] });

      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      const cachedList = qc.getQueryData<TodoListDto>(['todo-list', input.listId]);
      if (cachedList) snapshots.push({ key: ['todo-list', input.listId], data: cachedList });
      qc.getQueriesData<TodoListDto[]>({ queryKey: ['todos', input.groupId] }).forEach(([k, d]) => {
        if (d) snapshots.push({ key: k, data: d });
      });

      const removeItem = (l: TodoListDto): TodoListDto =>
        l.id === input.listId ? { ...l, items: l.items.filter((i) => i.id !== input.itemId) } : l;

      if (cachedList)
        qc.setQueryData<TodoListDto>(['todo-list', input.listId], removeItem(cachedList));
      qc.setQueriesData<TodoListDto[]>({ queryKey: ['todos', input.groupId] }, (list) =>
        list ? list.map(removeItem) : list,
      );

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (_d, _err, input) => {
      void qc.invalidateQueries({ queryKey: ['todo-list', input.listId] });
      void qc.invalidateQueries({ queryKey: ['todos', input.groupId] });
      void qc.invalidateQueries({ queryKey: ['public-todo'] });
    },
  });
}

// ─────────────────────────── Notifications (cf. ADR-023) ────────────────

/**
 * Source unique : `NotificationKindSchema` de `@nexus/shared` — le meme enum
 * que celui utilise par le backend pour produire les notifs.
 *
 * Cet enum etait auparavant redeclare ici. La duplication est un piege a
 * panne : `NotificationListReply` parse la liste avec `z.array(...)`, qui
 * jette des le PREMIER element inconnu — un `kind` ajoute cote backend et
 * oublie ici faisait donc disparaitre la TOTALITE des notifications de
 * l'utilisateur, pas seulement la ligne concernee (constate en revue
 * MAN-182 avec `member_removed`). Importer le schema partage supprime la
 * derive intra-repo et fait echouer le build (Records exhaustifs sur
 * `NotificationKind`) plutot que le runtime.
 */
const NotificationKindEnum = NotificationKindSchema;
export type NotificationKind = z.infer<typeof NotificationKindEnum>;

const NotificationSchema = z.object({
  id: z.string().uuid(),
  kind: NotificationKindEnum,
  payload: z.record(z.string(), z.unknown()),
  groupId: z.string().uuid().nullable(),
  sourceId: z.string().uuid().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type NotificationDto = z.infer<typeof NotificationSchema>;

const NotificationListReply = z.object({
  notifications: z.array(NotificationSchema),
  unreadCount: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
});

const MarkReadReply = z.object({
  ok: z.literal(true),
  markedCount: z.number().int().nonnegative(),
});

/**
 * Liste paginée des notifs du user courant. Fetch tout (50 par page) au
 * premier chargement ; la pagination cursor sera utilisée si on ajoute
 * scroll infini en V2.
 */
export function useNotifications(opts: { unread?: boolean; limit?: number } = {}) {
  const { unread, limit = 50 } = opts;
  const params = new URLSearchParams();
  if (unread) params.set('unread', 'true');
  if (limit) params.set('limit', String(limit));
  const queryString = params.toString();
  return useQuery({
    queryKey: ['notifications', { unread: !!unread, limit }],
    queryFn: async () =>
      api({
        method: 'GET',
        path: `/notifications${queryString ? '?' + queryString : ''}`,
        reply: NotificationListReply,
      }),
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { notificationId: string }) =>
      api({
        method: 'POST',
        path: `/notifications/${input.notificationId}/read`,
        body: {},
        reply: MarkReadReply,
      }),
    // ─── Optimistic update ───────────────────────────────────────────────
    // Le user clique une notif → badge unread (cloche) doit baisser
    // instantanément. On patche TOUTES les variantes de query
    // `['notifications', ...]` (car la cloche utilise `useNotifications()`
    // sans filtre, mais le panel pourrait avoir des filtres différents).
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['notifications'] });

      type NotifReply = z.infer<typeof NotificationListReply>;
      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      qc.getQueriesData<NotifReply>({ queryKey: ['notifications'] }).forEach(([k, d]) => {
        if (d) snapshots.push({ key: k, data: d });
      });

      const nowIso = new Date().toISOString();
      qc.setQueriesData<NotifReply>({ queryKey: ['notifications'] }, (cur) => {
        if (!cur) return cur;
        let wasUnread = false;
        const notifications = cur.notifications.map((n) => {
          if (n.id !== input.notificationId) return n;
          if (n.readAt === null) wasUnread = true;
          return { ...n, readAt: n.readAt ?? nowIso };
        });
        return {
          ...cur,
          notifications,
          unreadCount: wasUnread ? Math.max(0, cur.unreadCount - 1) : cur.unreadCount,
        };
      });

      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api({
        method: 'POST',
        path: `/notifications/read-all`,
        body: {},
        reply: MarkReadReply,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

const ClearAllReply = z.object({
  ok: z.literal(true),
  deletedCount: z.number().int().nonnegative(),
});

/**
 * Vide la liste : supprime TOUTES les notifs (read + unread) du user.
 * Action utilisateur explicite, irréversible. Les events/expenses/todos
 * sous-jacents restent en place — seul l'historique de notifs disparaît.
 */
export function useClearAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api({
        method: 'DELETE',
        path: `/notifications`,
        reply: ClearAllReply,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ───────────────────────────── Home feed (cf. ADR-024) ──────────────────────

const HomePendingRsvp = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsAt: z.string(),
  groupId: z.string().uuid(),
  groupName: z.string(),
});
const HomeUnsettledExpense = z.object({
  id: z.string().uuid(),
  description: z.string(),
  amountCents: z.number().int().nonnegative(),
  shareCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  paidById: z.string().uuid(),
  paidByName: z.string(),
  groupId: z.string().uuid(),
  groupName: z.string(),
});
const HomeAssignedTodo = z.object({
  id: z.string().uuid(),
  text: z.string(),
  listId: z.string().uuid(),
  listTitle: z.string(),
  groupId: z.string().uuid(),
  groupName: z.string(),
});
const HomeUpcomingEvent = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsAt: z.string(),
  location: z.string().nullable(),
  groupId: z.string().uuid(),
  groupName: z.string(),
});
const HomePendingPoll = z.object({
  id: z.string().uuid(),
  question: z.string(),
  closesAt: z.string().nullable(),
  optionCount: z.number().int().nonnegative(),
  groupId: z.string().uuid(),
  groupName: z.string(),
});
const HomeGroupUnreadCount = z.object({
  groupId: z.string().uuid(),
  groupName: z.string(),
  count: z.number().int().nonnegative(),
});
/** Même forme qu'un `HomeUpcomingEvent` — c'est la sémantique qui diffère
 *  (tout ce que porte la semaine, vs mes 5 prochains confirmés). */
const HomeWeekEvent = HomeUpcomingEvent;
const HomeFeedReply = z.object({
  pendingRsvps: HomePendingRsvp.array(),
  unsettledExpenses: HomeUnsettledExpense.array(),
  assignedTodos: HomeAssignedTodo.array(),
  upcomingEvents: HomeUpcomingEvent.array(),
  // `.optional()` plutôt que `.array()` sec : au déploiement, web et backend ne
  // basculent pas à la même seconde. Un front neuf qui interroge un backend pas
  // encore à jour ferait autrement échouer le parse de TOUTE la Home pour un
  // champ absent — un calendrier vide une minute est un bien meilleur pire cas.
  // (`.default([])` serait plus confortable au point d'usage, mais `api()` prend
  // son `TReply` d'un `ZodType<T>`, dont l'Input et l'Output sont le même T :
  // un schéma dont les deux diffèrent y perd son typage de sortie.)
  weekEvents: HomeWeekEvent.array().optional(),
  pendingPolls: HomePendingPoll.array(),
  unreadByGroup: HomeGroupUnreadCount.array(),
});
export type HomeFeed = z.infer<typeof HomeFeedReply>;
export type HomePendingRsvpItem = z.infer<typeof HomePendingRsvp>;
export type HomeUnsettledExpenseItem = z.infer<typeof HomeUnsettledExpense>;
export type HomeAssignedTodoItem = z.infer<typeof HomeAssignedTodo>;
export type HomeUpcomingEventItem = z.infer<typeof HomeUpcomingEvent>;
export type HomeWeekEventItem = z.infer<typeof HomeWeekEvent>;
export type HomePendingPollItem = z.infer<typeof HomePendingPoll>;
export type HomeGroupUnreadItem = z.infer<typeof HomeGroupUnreadCount>;

/**
 * Récupère le feed agrégé Home (cf. ADR-024).
 *
 * Volume cible : top 5/10 par section, donc payload < 5 KB → on peut
 * polling agressivement (refetchOnWindowFocus + interval 60 s) sans soucis
 * de bande passante. Les notifs WS invalident déjà les caches concernés,
 * mais comme la Home agrège plusieurs sources, on garde un refetch périodique.
 *
 * Trois chemins rafraîchissent ce feed, et c'est voulu : mes propres mutations
 * (règle globale du `MutationCache`, cf. `lib/queryClient.ts`), celles des
 * autres (`useKillerFeaturesWs`), et le tick périodique ci-dessous — qui reste
 * le seul filet pour ce qu'aucun des deux ne couvre, comme un remplissage par
 * worker.
 *
 * Gaté sur l'auth et scopé par `userId`, exactement comme `useGroups` — cf.
 * ticket 10bc1096 : sans le `userId` dans la clé, celle de deux comptes
 * successifs sur la même machine est identique, et TanStack sert au second
 * le feed du premier.
 */
export function useHomeFeed(opts: { enabled?: boolean } = {}) {
  const userId = useAuth((s) => s.user?.id);
  const initializing = useAuth((s) => s.initializing);
  // Les bornes voyagent en query : c'est le fuseau de l'utilisateur qui définit
  // « cette semaine », pas celui du VPS (cf. `lib/week.ts`). Elles entrent aussi
  // dans la queryKey — sans ça, une app laissée ouverte du dimanche au lundi
  // resservirait indéfiniment le cache de la semaine écoulée.
  const week = currentWeekBounds();
  const weekStart = week.start.toISOString();
  const weekEnd = week.end.toISOString();
  const params = new URLSearchParams({ weekStart, weekEnd });
  return useQuery({
    // Préfixe partagé avec la règle d'invalidation : `HOME_QUERY_KEY` est la
    // racine que `createQueryClient` invalide. Un littéral local ici et la
    // règle deviendrait un no-op silencieux au premier renommage.
    queryKey: [...HOME_QUERY_KEY, 'feed', userId ?? null, weekStart],
    queryFn: async () =>
      api({ method: 'GET', path: `/home/feed?${params.toString()}`, reply: HomeFeedReply }),
    // Le `clear()` au changement d'identité (`useResetCacheOnUserChange`) est
    // la vraie ceinture ; le `userId` en clé est la bretelle, qui tient même
    // si un jour un chemin d'auth contourne ce hook. Le gate évite en prime le
    // 401 silencieux du cold load, sans refetch automatique derrière, que
    // `useGroups` documente.
    enabled: (opts.enabled ?? true) && !!userId && !initializing,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 15_000,
    // Au franchissement du lundi 00:00, la queryKey change et `data` repasserait
    // à `undefined` : `HomeDashboard` démonterait toute la Home derrière un
    // « Chargement… » le temps d'un aller-retour. On garde l'ancienne semaine
    // affichée pendant le refetch — au pire quelques centaines de ms d'une
    // semaine périmée, plutôt qu'un écran vide.
    placeholderData: (prev: HomeFeed | undefined) => prev,
  });
}

// ───────────────────────────── Activity feed (cf. ADR-029) ──────────────────

const ActivityKindEnum = z.enum([
  'event:created',
  'event:rsvp:changed',
  'event:cancelled',
  'poll:created',
  'poll:voted',
  'poll:closed',
  'expense:added',
  'expense:settled',
  'todo_list:created',
  'todo_item:checked',
  'todo_item:assigned',
  'member:joined',
  'member:left',
]);
export type ActivityKind = z.infer<typeof ActivityKindEnum>;

const ActivityTargetTypeEnum = z.enum([
  'event',
  'poll',
  'expense',
  'todo_list',
  'todo_item',
  'member',
]);
export type ActivityTargetType = z.infer<typeof ActivityTargetTypeEnum>;

const ActivityPayload = z
  .object({
    actorName: z.string().optional(),
    targetTitle: z.string().optional(),
    groupName: z.string().optional(),
    rsvp: z.string().optional(),
    amountCents: z.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    optionLabel: z.string().optional(),
    itemText: z.string().optional(),
    assigneeName: z.string().optional(),
  })
  .catchall(z.unknown());
export type ActivityPayloadDto = z.infer<typeof ActivityPayload>;

const ActivityItem = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  groupName: z.string(),
  actorId: z.string().uuid().nullable(),
  kind: ActivityKindEnum,
  targetId: z.string().uuid().nullable(),
  targetType: ActivityTargetTypeEnum,
  payload: ActivityPayload,
  createdAt: z.string().datetime(),
});
export type ActivityItemDto = z.infer<typeof ActivityItem>;

const ActivityFeedReply = z.object({
  items: ActivityItem.array(),
  nextCursor: z.string().datetime().nullable(),
});
export type ActivityFeedPage = z.infer<typeof ActivityFeedReply>;

/**
 * Récupère le feed d'activité paginé (cf. ADR-029).
 *
 * Sans filtre : timeline cross-groupes (Home Nexus).
 * Avec `groupId` : timeline d'un groupe (GroupHomeDashboard).
 *
 * Pagination cursor-based : `fetchNextPage()` charge la page suivante,
 * `nextCursor === null` signifie "fin de l'historique".
 */
export function useActivityFeed(opts: { groupId?: string; enabled?: boolean } = {}) {
  const { groupId, enabled = true } = opts;
  return useInfiniteQuery({
    queryKey: ['activity-feed', groupId ?? 'all'],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const params = new URLSearchParams();
      if (groupId) params.set('groupId', groupId);
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      return api({
        method: 'GET',
        path: `/activity-feed${qs ? `?${qs}` : ''}`,
        reply: ActivityFeedReply,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
}

// ─────────────────────── Préférences de notification (ADR-034) ───────────────

/**
 * Miroir de `NotificationPrefsDtoSchema` côté backend
 * (routes/notifications/schemas.ts) : un booléen par `kind`. À terme via
 * @nexus/shared (dette J4b-bis).
 */
const NotificationPrefsSchema = z.object({
  eventReminder: z.boolean(),
  eventRsvpRequested: z.boolean(),
  eventRsvpReceived: z.boolean(),
  expenseAdded: z.boolean(),
  todoAssigned: z.boolean(),
  todoCompleted: z.boolean(),
  updatedAt: z.string(),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;
/** Clés togglables (sans `updatedAt`). */
export type NotificationPrefKey = Exclude<keyof NotificationPrefs, 'updatedAt'>;
const NotificationPrefsReply = z.object({ preferences: NotificationPrefsSchema });

export function useNotificationPrefs() {
  const userId = useAuth((s) => s.user?.id);
  return useQuery({
    enabled: !!userId,
    queryKey: ['notification-prefs', userId ?? null],
    queryFn: async () =>
      api({
        method: 'GET',
        path: '/notifications/preferences',
        reply: NotificationPrefsReply,
      }).then((r) => r.preferences),
    staleTime: 60_000,
  });
}

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Record<NotificationPrefKey, boolean>>) =>
      api({
        method: 'PATCH',
        path: '/notifications/preferences',
        body: patch,
        reply: NotificationPrefsReply,
      }).then((r) => r.preferences),
    onSuccess: (prefs) => {
      const userId = useAuth.getState().user?.id ?? null;
      qc.setQueryData(['notification-prefs', userId], prefs);
    },
  });
}
